/**
 * @file HomePage.jsx
 * @description Redesigned home page with Material Design 3 - shows different content for signed-in vs signed-out users
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { collection, getDocs, getDocsFromServer, doc, getDoc, getDocFromServer } from "firebase/firestore";
import { logIn } from "../UserAuth";
import { countArchivedUserDecks, loadUserDecks } from "../utils/deck_service";
import { hasCompletedTodaysReading } from "../utils/visual_utils";
import { 
  getNotificationSettings, 
  scheduleDailyNotification,
  canSendNotifications 
} from "../utils/notificationUtils";
import { useViewportHeight } from "../utils/viewportUtils";
import { Icon } from "./Icon";

export const HomePage = () => {
  const authState = useAuthState(auth);
  const [user, loading] = authState || [null, false, null];
  const [userChallenges, setUserChallenges] = useState([]);
  const [userDecks, setUserDecks] = useState([]);
  const [decksLoading, setDecksLoading] = useState(true);
  const [hasArchivedDecks, setHasArchivedDecks] = useState(false);
  const [hasArchivedChallenges, setHasArchivedChallenges] = useState(false);
  const [challengesWithIncompleteReadings, setChallengesWithIncompleteReadings] = useState(new Set());

  const cacheChallenges = useCallback((challenges, hasArchived) => {
    try {
      const cacheKey = `userChallenges_${user.uid}`;
      const cacheData = {
        challenges,
        hasArchived,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching challenges:', error);
    }
  }, [user]);

  const checkChallengesProgress = useCallback((challenges) => {
    const incompleteSet = new Set();
    
    challenges.forEach(challenge => {
      if (!hasCompletedTodaysReading(challenge, challenge.progress)) {
        incompleteSet.add(challenge.id);
      }
    });
    
    setChallengesWithIncompleteReadings(incompleteSet);
  }, []);

  const scheduleNotificationsForChallenges = useCallback((challenges) => {
    try {
      const notificationSettings = getNotificationSettings();
      
      if (canSendNotifications() && notificationSettings.enabled) {
        scheduleDailyNotification(notificationSettings.time, challenges);
      }
    } catch (error) {
      console.error('Error scheduling notifications:', error);
    }
  }, []);

  const loadCachedChallenges = useCallback(() => {
    try {
      const cacheKey = `userChallenges_${user.uid}`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const { challenges, hasArchived, timestamp } = JSON.parse(cachedData);
        // Use cache if it's less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          setUserChallenges(challenges);
          setHasArchivedChallenges(hasArchived);
          // Check progress for cached challenges
          checkChallengesProgress(challenges);
          // Schedule notifications for cached challenges
          scheduleNotificationsForChallenges(challenges);
        }
      }
    } catch (error) {
      console.error('Error loading cached challenges:', error);
    }
  }, [user, checkChallengesProgress, scheduleNotificationsForChallenges]);

  const loadCachedDecks = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(`userDecks_${user.uid}`);
      if (!cachedData) {
        return;
      }
      const { decks, hasArchived, timestamp } = JSON.parse(cachedData);
      if (Array.isArray(decks) && Date.now() - timestamp < 5 * 60 * 1000) {
        setUserDecks(decks);
        setHasArchivedDecks(hasArchived === true);
        setDecksLoading(false);
      }
    } catch (error) {
      console.error('Error loading cached decks:', error);
    }
  }, [user]);

  const fetchUserDecks = useCallback(async () => {
    try {
      const [decks, archivedCount] = await Promise.all([
        loadUserDecks(db, user.uid),
        countArchivedUserDecks(db, user.uid),
      ]);
      setUserDecks(decks);
      setHasArchivedDecks(archivedCount > 0);
      localStorage.setItem(`userDecks_${user.uid}`, JSON.stringify({
        decks,
        hasArchived: archivedCount > 0,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('Error fetching user decks:', error);
    } finally {
      setDecksLoading(false);
    }
  }, [user]);

  const fetchUserChallenges = useCallback(async (options = {}) => {
    const { forceServer = false } = options;
    try {
      // Get user's challenge refs from users/{uid}/challenges
      const userChallengesRef = collection(db, "users", user.uid, "challenges");
      const userChallengesSnapshot = await (forceServer 
        ? getDocsFromServer(userChallengesRef) 
        : getDocs(userChallengesRef)
      );
      
      // Get the actual challenge data from challenges collection
      const challengePromises = userChallengesSnapshot.docs.map(async (userChallengeDoc) => {
        const challengeId = userChallengeDoc.id;
        const userChallengeData = userChallengeDoc.data();
        
        // Fetch the actual challenge data
        const challengeDocRef = doc(db, "challenges", challengeId);
        const challengeDoc = await (forceServer
          ? getDocFromServer(challengeDocRef)
          : getDoc(challengeDocRef)
        );
        
        if (challengeDoc.exists()) {
          return {
            id: challengeId,
            ...challengeDoc.data(),
            ...userChallengeData // Include user-specific data like archived status
          };
        }
        return null;
      });
      
      const challenges = (await Promise.all(challengePromises)).filter(Boolean);
      
      const activeChallenges = challenges.filter(c => !c.archived);
      const archivedChallenges = challenges.filter(c => c.archived);
      const hasArchived = archivedChallenges.length > 0;
      
      setUserChallenges(activeChallenges);
      setHasArchivedChallenges(hasArchived);
      
      // Check progress for active challenges
      checkChallengesProgress(activeChallenges);
      
      // Cache the results
      cacheChallenges(activeChallenges, hasArchived);
      
      // Schedule notifications if enabled
      scheduleNotificationsForChallenges(activeChallenges);
    } catch (error) {
      console.error("Error fetching user challenges:", error);
    }
  }, [user, checkChallengesProgress, cacheChallenges, scheduleNotificationsForChallenges]);

  useEffect(() => {
    if (user) {
      const shouldForceRefresh = sessionStorage.getItem('refreshChallenges') === 'true';
      if (shouldForceRefresh) {
        sessionStorage.removeItem('refreshChallenges');
      } else {
        loadCachedChallenges();
      }
      loadCachedDecks();
      fetchUserChallenges({ forceServer: shouldForceRefresh });
      fetchUserDecks();
    }
  }, [
    user,
    loadCachedChallenges,
    loadCachedDecks,
    fetchUserChallenges,
    fetchUserDecks,
  ]);

  if (loading) {
    return (
      <div className="app-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <md-circular-progress indeterminate></md-circular-progress>
      </div>
    );
  }

  if (!user) {
    return <SignedOutHomePage />;
  }

  return <SignedInHomePage 
    userChallenges={userChallenges} 
    userDecks={userDecks}
    decksLoading={decksLoading}
    hasArchivedDecks={hasArchivedDecks}
    hasArchivedChallenges={hasArchivedChallenges}
    challengesWithIncompleteReadings={challengesWithIncompleteReadings}
  />;
};

const SignedOutHomePage = () => {
  const viewportHeight = useViewportHeight();
  
  return (
    <div className="app-content" style={{ 
      height: `calc(${viewportHeight} - 64px)`, // Account for header height
      overflow: 'hidden',
      padding: '8px'
    }}>
      <div className="container" style={{ 
        maxWidth: '1200px',
        height: '100%',
        margin: '0 auto',
        padding: '0 8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'auto',
        minHeight: 0
      }}>
        {/* Welcome Header */}
        <div style={{
          textAlign: 'center',
          padding: '16px 0',
          flexShrink: 0
        }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '500',
            color: 'var(--md-sys-color-on-surface)',
            margin: '0'
          }}>
            Welcome to memorize.life!
          </h1>
        </div>

        {/* Content Areas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
          flex: '1 0 auto',
          alignContent: 'center'
        }}>
          {/* Bible Reading Challenges */}
          <md-elevated-card style={{ padding: '16px', height: 'fit-content' }}>
            <div style={{ marginBottom: '12px', textAlign: 'center' }}>
              <Icon name="book" style={{ 
                color: 'var(--md-sys-color-primary)',
                marginBottom: '16px'
              }} size="hero" />
              <h2 style={{ 
                fontSize: 'var(--md-sys-typescale-headline-large-size)',
                fontWeight: 'var(--md-sys-typescale-headline-large-weight)',
                color: 'var(--md-sys-color-on-surface)',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                Bible Reading Challenges
              </h2>
            </div>
            <p style={{ 
              color: 'var(--md-sys-color-on-surface-variant)',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Choose a book of the Bible to read, what days to read on, and how fast to read. 
              By sharing a link to your challenge, others can join your challenge and read with you.
            </p>
          </md-elevated-card>

          {/* Memorization Decks */}
          <md-elevated-card style={{ padding: '16px', height: 'fit-content' }}>
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
              <Icon name="brain" style={{ 
                color: 'var(--md-sys-color-tertiary)',
                marginBottom: '16px'
              }} size="hero" />
              <h2 style={{ 
                fontSize: 'var(--md-sys-typescale-headline-large-size)',
                fontWeight: 'var(--md-sys-typescale-headline-large-weight)',
                color: 'var(--md-sys-color-on-surface)',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                Memorization Decks
              </h2>
            </div>
            <p style={{ 
              color: 'var(--md-sys-color-on-surface-variant)',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Build a lasting memory of Scripture with shareable decks, guided practice,
              and spaced review.
            </p>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <md-filled-tonal-button href="/join-decks">
                Discover Memorization Decks
              </md-filled-tonal-button>
            </div>
          </md-elevated-card>
        </div>

        {/* Sign In Section */}
        <div style={{
          textAlign: 'center',
          padding: '16px 0',
          borderTop: '1px solid var(--md-sys-color-outline-variant)',
          flexShrink: 0
        }}>
          <md-filled-button 
            onClick={logIn}
            style={{ marginBottom: '16px' }}
          >
            <Icon name="signInGoogle" slot="icon" />
            Sign In with Google
          </md-filled-button>
          <br />
          <md-text-button href="/about">
            About this site
          </md-text-button>
        </div>
      </div>
    </div>
  );
};

const DeckActionCard = ({ href, icon, title, description }) => {
  const history = useHistory();

  return (
    <a
      href={href}
      onClick={event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        history.push(href);
      }}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        height: 'fit-content'
      }}
    >
      <md-elevated-card style={{ padding: '16px', height: 'fit-content' }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '500',
          color: 'var(--md-sys-color-on-surface)',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Icon name={icon} ariaHidden />
          {title}
        </h2>
        <p style={{
          color: 'var(--md-sys-color-on-surface-variant)',
          lineHeight: '1.4',
          marginBottom: 0,
          fontSize: '14px'
        }}>
          {description}
        </p>
      </md-elevated-card>
    </a>
  );
};

const RedDotIndicator = () => (
  <div style={{
    width: '8px',
    height: '8px',
    backgroundColor: 'var(--md-sys-color-error)',
    borderRadius: '50%',
    marginLeft: '4px',
    flexShrink: 0
  }} />
);

const getDeckName = deck => (
  typeof deck?.deck_name === 'string' && deck.deck_name.trim()
    ? deck.deck_name.trim()
    : 'Untitled Deck'
);

const getDeckVerseCount = deck => (
  Number.isInteger(deck?.verse_count) && deck.verse_count > 0
    ? deck.verse_count
    : 0
);

const DeckProgressIndicator = ({ deck }) => {
  const verseCount = getDeckVerseCount(deck);
  const learnedCount = Math.min(
    verseCount,
    Math.max(0, Number.isInteger(deck?.learned_count) ? deck.learned_count : 0)
  );
  const retainedCount = Math.min(
    learnedCount,
    Math.max(0, Number.isInteger(deck?.retained_count) ? deck.retained_count : 0)
  );
  const learnedPercent = verseCount === 0 ? 0 : Math.round((learnedCount / verseCount) * 100);
  const retainedPercent = verseCount === 0 ? 0 : Math.round((retainedCount / verseCount) * 100);
  const progressIsCurrent = Number.isInteger(deck?.content_revision)
    && deck.progress_revision === deck.content_revision;
  const retentionIsCurrent = Number.isFinite(deck?.retained_next_expiry_at)
    ? deck.retained_next_expiry_at > Date.now()
    : retainedCount === 0;
  const label = !progressIsCurrent
    ? `${learnedPercent}% learned from the previous deck revision; progress is updating`
    : `${learnedPercent}% learned, ${retainedPercent}% retained${
      retentionIsCurrent ? '' : ' as of your last session'
    }`;

  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      data-testid={`deck-progress-${deck.id}`}
      style={{
        width: '48px',
        height: '48px',
        position: 'relative',
        flexShrink: 0,
        opacity: progressIsCurrent ? 1 : 0.55,
      }}
    >
      <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
        <circle
          cx="24"
          cy="24"
          r="19"
          fill="none"
          stroke="var(--md-sys-color-surface-container-highest)"
          strokeWidth="5"
        />
        <circle
          cx="24"
          cy="24"
          r="19"
          fill="none"
          pathLength="100"
          stroke="var(--md-sys-color-secondary)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${learnedPercent} 100`}
          transform="rotate(-90 24 24)"
          data-testid="learned-progress-ring"
        />
        <circle
          cx="24"
          cy="24"
          r="19"
          fill="none"
          pathLength="100"
          stroke="var(--md-sys-color-primary)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${retainedPercent} 100`}
          strokeOpacity={retentionIsCurrent ? 1 : 0.5}
          transform="rotate(-90 24 24)"
          data-testid="retained-progress-ring"
        />
      </svg>
      <span style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        fontSize: '11px',
        fontWeight: 700,
        color: 'var(--md-sys-color-on-surface)',
      }}>
        {learnedPercent}%
      </span>
    </div>
  );
};

const SignedInHomePage = ({
  userChallenges,
  userDecks,
  decksLoading,
  hasArchivedDecks,
  hasArchivedChallenges,
  challengesWithIncompleteReadings,
}) => {
  const history = useHistory();
  const viewportHeight = useViewportHeight();
  const hasAnyIncompleteReadings = challengesWithIncompleteReadings.size > 0;
  const sortedChallenges = useMemo(() => {
    return userChallenges
      .map((challenge, index) => ({
        challenge,
        index,
        hasIncomplete: challengesWithIncompleteReadings.has(challenge.id)
      }))
      .sort((a, b) => {
        if (a.hasIncomplete === b.hasIncomplete) {
          return a.index - b.index;
        }
        return a.hasIncomplete ? -1 : 1;
      })
      .map(({ challenge }) => challenge);
  }, [userChallenges, challengesWithIncompleteReadings]);
  return (
    <div className="app-content" style={{ 
      height: `calc(${viewportHeight} - 64px)`, // Account for header height
      overflow: 'hidden',
      padding: '8px'
    }}>
      <div className="container" style={{ 
        maxWidth: '1200px',
        height: '100%',
        margin: '0 auto',
        padding: '0 8px',
        overflowY: 'auto',
        minHeight: 0,
      }}>

        {/* Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
          height: '100%',
          alignContent: 'start'
        }}>
          {/* Current Challenges - Only show if user has challenges */}
          {userChallenges.length > 0 && (
            <md-elevated-card style={{ padding: '16px', height: 'fit-content' }}>
              <h2 style={{ 
                fontSize: '18px',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="bookList" />
                Current Challenges
                {hasAnyIncompleteReadings && <RedDotIndicator />}
              </h2>
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {sortedChallenges.map((challenge) => (
                  <md-list-item 
                    key={challenge.id}
                    href={`/challenge?id=${challenge.id}`}
                    style={{ 
                      backgroundColor: 'var(--md-sys-color-surface-container-low)',
                      borderRadius: '8px'
                    }}
                  >
                    <Icon name="book" slot="start" />
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1
                    }}>
                      <span style={{
                        fontSize: 'var(--md-sys-typescale-body-large-size)',
                        fontWeight: 'var(--md-sys-typescale-body-large-weight)',
                        lineHeight: 'var(--md-sys-typescale-body-large-line-height)',
                        color: 'var(--md-sys-color-on-surface)',
                        flex: 1
                      }}>
                        {challenge.challenge_name || challenge.title || challenge.name || 'Untitled Challenge'}
                      </span>
                      {challengesWithIncompleteReadings.has(challenge.id) && <RedDotIndicator />}
                    </div>
                  </md-list-item>
                ))}
              </div>
            </md-elevated-card>
          )}

          {(decksLoading || userDecks.length > 0) && (
            <md-elevated-card style={{ padding: '16px', height: 'fit-content' }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Icon name="memorizeTab" />
                Current Decks
              </h2>
              {decksLoading && userDecks.length === 0 ? (
                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <md-circular-progress indeterminate></md-circular-progress>
                  Loading decks…
                </div>
              ) : (
                <div style={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                  {userDecks.map(deck => (
                    <md-list-item
                      key={deck.id}
                      href={`/deck?id=${deck.id}`}
                      style={{
                        backgroundColor: 'var(--md-sys-color-surface-container-low)',
                        borderRadius: '8px',
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: '12px',
                      }}>
                        <span style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {getDeckName(deck)}
                        </span>
                        <DeckProgressIndicator deck={deck} />
                      </div>
                    </md-list-item>
                  ))}
                </div>
              )}
            </md-elevated-card>
          )}

          {/* Make a Reading Challenge */}
          <md-elevated-card 
            style={{ 
              padding: '16px', 
              height: 'fit-content',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
              textDecoration: 'none',
              color: 'inherit'
            }}
            onClick={() => history.push('/create_challenge')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--md-sys-elevation-level3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <h2 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: 'var(--md-sys-color-on-surface)',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon name="addChallenge" />
              Make a Reading Challenge
            </h2>
            <p style={{
              color: 'var(--md-sys-color-on-surface-variant)',
              lineHeight: '1.4',
              marginBottom: '0',
              fontSize: '14px'
            }}>
              Create a reading challenge to read the Bible with others!
            </p>
          </md-elevated-card>

          <DeckActionCard
            href="/create-deck"
            icon="createDeck"
            title="Create a Memorization Deck"
            description="Choose Scripture and a pace for guided memorization."
          />

          <DeckActionCard
            href="/join-decks"
            icon="joinDeck"
            title="Join a Memorization Deck"
            description="Discover public decks and memorize with others."
          />

          {hasArchivedDecks && (
            <DeckActionCard
              href="/archived-decks"
              icon="archive"
              title="View Archived Decks"
              description="Restore a memorization deck you archived earlier."
            />
          )}

          {/* Join a Reading Challenge */}
          <md-elevated-card 
            style={{ 
              padding: '16px', 
              height: 'fit-content',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
              textDecoration: 'none',
              color: 'inherit'
            }}
            onClick={() => history.push('/join-challenges')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--md-sys-elevation-level3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <h2 style={{ 
              fontSize: '18px',
              fontWeight: '500',
              color: 'var(--md-sys-color-on-surface)',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon name="joinChallenge" />
              Join a Reading Challenge
            </h2>
            <p style={{
              color: 'var(--md-sys-color-on-surface-variant)',
              lineHeight: '1.4',
              marginBottom: '0',
              fontSize: '14px'
            }}>
              Find an existing public challenge to join.
            </p>
          </md-elevated-card>

          {/* View Archived Challenges - Only show if user has archived challenges */}
          {hasArchivedChallenges && (
            <md-elevated-card 
              style={{ 
                padding: '16px', 
                height: 'fit-content',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                textDecoration: 'none',
                color: 'inherit'
              }}
              onClick={() => history.push('/archived-challenges')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--md-sys-elevation-level3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <h2 style={{ 
                fontSize: '18px',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="archive" />
                View Archived Challenges
              </h2>
              <p style={{
                color: 'var(--md-sys-color-on-surface-variant)',
                lineHeight: '1.4',
                marginBottom: '0',
                fontSize: '14px'
              }}>
                View your completed and archived reading challenges.
              </p>
            </md-elevated-card>
          )}
        </div>
      </div>
    </div>
  );
};
